import { useState } from 'react';

import Button from '@ingka/button';
import SSRIcon from '@ingka/ssr-icon';
import calendar from '@ingka/ssr-icon/paths/calendar';
import chevronDownIcon from '@ingka/ssr-icon/paths/chevron-down';
import chevronUpIcon from '@ingka/ssr-icon/paths/chevron-up';
import deliveryTruck from '@ingka/ssr-icon/paths/delivery-truck';
import drill from '@ingka/ssr-icon/paths/drill';
import palletLoaded from '@ingka/ssr-icon/paths/pallet-loaded';
import Text from '@ingka/text';
import { translate } from '@repo/phrase';
import { NoBodyMessage } from '@repo/ui';

import { FulfillmentArticles } from '../fulfillment-articles/fulfillment-articles.component';
import type { FulfillmentService } from '../fulfillment.types';

import styles from './styles.module.scss';

interface FulfillmentViewProps {
    readonly services: readonly FulfillmentService[];
}

export const FulfillmentView = ({ services }: FulfillmentViewProps) => {
    const [collapsedServiceIds, setCollapsedServiceIds] = useState<readonly string[]>([]);

    if (services.length === 0) {
        return (
            <NoBodyMessage
                title={translate('services_not_found_title')}
                body={translate('services_not_found_body')}
                iconAriaLabel={translate('services_not_found_icon')}
                headingLevel="2"
            />
        );
    }

    const toggleService = (id: string) => {
        setCollapsedServiceIds((prev) =>
            prev.includes(id) ? prev.filter((serviceId) => serviceId !== id) : [...prev, id]
        );
    };

    return (
        <>
            {services.map((service) => {
                const isCollapsed = collapsedServiceIds.includes(service.id);

                return (
                    <div key={service.id}>
                        <div className={styles.service}>
                            {service.articles.length > 0 && (
                                <Button
                                    iconOnly
                                    size="small"
                                    type="tertiary"
                                    ssrIcon={isCollapsed ? chevronDownIcon : chevronUpIcon}
                                    onClick={() => {
                                        toggleService(service.id);
                                    }}
                                    aria-label={translate(
                                        isCollapsed ? 'fulfillment_service_expand' : 'fulfillment_service_collapse'
                                    )}
                                    aria-expanded={!isCollapsed}
                                />
                            )}
                            <SSRIcon
                                className={String(styles.icon)}
                                paths={
                                    service.isFullServeHandoutDelivery
                                        ? palletLoaded
                                        : service.isDelivery
                                          ? deliveryTruck
                                          : drill
                                }
                            />
                            <div className={styles.summary}>
                                <Text tagName="span" textStyle="Heading.XS" className={String(styles.service_name)}>
                                    {service.serviceName}
                                </Text>
                                {service.summaryDetails && (
                                    <Text
                                        tagName="span"
                                        textStyle="Body.M"
                                        colour={false}
                                        className={String(styles.summary_details)}
                                    >
                                        {service.summaryDetails}
                                    </Text>
                                )}
                            </div>
                            {service.price && (
                                <Text tagName="span" textStyle="Heading.XS" className={String(styles.price)}>
                                    {service.price}
                                </Text>
                            )}
                            {service.timeWindow?.onClick ? (
                                <Button
                                    type="secondary"
                                    size="small"
                                    text={service.timeWindow.value}
                                    ssrIcon={calendar}
                                    iconPosition="leading"
                                    className={String(styles.time_window)}
                                    onClick={service.timeWindow.onClick}
                                />
                            ) : (
                                service.timeWindow && (
                                    <span className={`${styles.time_window} ${styles.readonly_time_window}`}>
                                        <SSRIcon
                                            paths={calendar}
                                            className={String(styles.time_window_icon)}
                                            aria-hidden={true}
                                            data-testid="fulfillment-time-window-icon"
                                        />
                                        <Text tagName="span" textStyle="Label.S" colour={false}>
                                            {service.timeWindow.value}
                                        </Text>
                                    </span>
                                )
                            )}
                        </div>
                        {!isCollapsed && <FulfillmentArticles articles={service.articles} />}
                    </div>
                );
            })}
        </>
    );
};
